import type { InputProps } from '@byecode/ui'
import { IconFont, Input, itemBackgroundTransitionOnClick, Popover, SelectDropdown } from '@byecode/ui'
import type { RectSizeUnit } from '@lighthouse/core'
import isDeepEqual from 'fast-deep-equal'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { transform2Integer, transform2Number } from '@/utils/number'

import { SizeIcon } from '../SizeSetting/SizeIcon'

const SCxRectSizeInputWrapper = styled.div`
    display: flex;
    width: 180px;
`

const SCxDropdownButton = styled.div`
    display: flex;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    background-color: var(--color-gray-100);
    border-left: 1px solid var(--color-gray-200);
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    cursor: pointer;

    ${itemBackgroundTransitionOnClick}

    &:hover {
        background-color: var(--color-gray-200);
    }
`

type SizeInputValue = {
    size?: number
    unit?: Exclude<RectSizeUnit, 'vw' | 'vh'>
}

interface LayoutSizeInputProps {
    min?: number
    max?: number
    iconType: string
    value?: SizeInputValue
    onChange: (value: SizeInputValue) => void
    onChangeMove?: (v: boolean) => void
    onUnitChange?: (unit: NonNullable<SizeInputValue['unit']>, prevValue?: SizeInputValue) => SizeInputValue

    inputProps?: InputProps
    disableUnitChange?: boolean
    disablePercent?: boolean
    disableRem?: boolean
}

export const BASE_DROPDOWN_OPTIONS = [
    { label: '像素', value: 'px' as const, icon: 'px' },
    { label: `百分比`, value: '%' as const, icon: 'PropertyPercent' },
    { label: `相对根元素`, value: 'rem' as const, icon: 'Rem' }
]

export const LayoutSizeInput: React.FC<LayoutSizeInputProps> = ({
    min = 0,
    max,
    iconType,
    value,
    onChange,
    onChangeMove,
    onUnitChange,
    inputProps,
    disableUnitChange,
    disablePercent,
    disableRem
}) => {
    const [moveOpen, setMoveOpen] = useState(false)
    const [dropdownOpened, setDropdownOpened] = useState(false)
    const init = useMemo(() => value ?? { size: undefined, unit: undefined }, [value])
    const [v, setV] = useImmer<SizeInputValue>(init)

    useUpdateEffect(() => {
        if (init?.size === v.size && init?.unit === v.unit) {
            return
        }
        setV(init)
    }, [init.size])

    const handleMoveChange = useCallback(
        (v: number) => {
            const num = transform2Integer(v)
            const val: SizeInputValue = {
                size: num,
                unit: value?.unit || 'px'
            }
            setV(val)
            if (isDeepEqual(value, val)) {
                return
            }
            onChange(val)
        },
        [onChange, setV, value]
    )

    const handleChange = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const val = ev.target.value
            const size = val ? transform2Number(val) : 0
            setV(draft => {
                draft.size = size
            })
        },
        [setV]
    )

    const handleItemClick = useCallback(
        (unit: SizeInputValue['unit']) => {
            if (disableUnitChange || !onUnitChange) {
                return
            }
            switch (unit) {
                case 'px':
                case 'rem':
                case '%': {
                    const val = onUnitChange(unit, value)
                    setV(val)
                    if (isDeepEqual(value, val)) {
                        return
                    }
                    onChange(val)
                    break
                }

                default: {
                    break
                }

                // No default
            }
            setDropdownOpened(false)
        },
        [disableUnitChange, onUnitChange, value, setV, onChange]
    )

    const handleBlur = useCallback(
        (ev: React.FocusEvent<HTMLInputElement>) => {
            const v = ev.target.value
            const number = transform2Number(v)
            const validNumber = number > 0 ? number : 0
            const limitNumber = transform2Number(validNumber.toFixed(2))
            const val = { size: limitNumber, unit: value?.unit || 'px' }
            if (isDeepEqual(val, value)) {
                return
            }
            onChange(val)
        },
        [onChange, value]
    )

    const handleToggleMove = useCallback(
        (v: boolean) => {
            setMoveOpen(v)
            onChangeMove?.(v)
        },
        [onChangeMove]
    )

    const dropdownOptions = useMemo(() => {
        const disabledSet = new Set<SizeInputValue['unit']>()
        if (disableRem) {
            disabledSet.add('rem')
        }
        if (disablePercent) {
            disabledSet.add('%')
        }
        return BASE_DROPDOWN_OPTIONS.filter(item => !disabledSet.has(item.value))
    }, [disablePercent, disableRem])

    const resizeStyle = useMemo(
        () =>
            moveOpen
                ? {
                      borderColor: 'var(--color-main)',
                      backgroundColor: 'var(--color-gray-200)'
                  }
                : {},
        [moveOpen]
    )

    return (
        <SCxRectSizeInputWrapper>
            <Input
                focusOutLine
                value={v?.size}
                prefix={<SizeIcon value={v.size} min={min} type={iconType} onChangeMove={handleToggleMove} onChange={handleMoveChange} />}
                suffix={value?.unit}
                radius={0}
                min={min}
                max={max}
                type="number"
                onFocus={e => e.currentTarget.select()}
                styles={{
                    wrapper: {
                        borderTopRightRadius: '0!important',
                        borderBottomRightRadius: '0!important',
                        ...resizeStyle
                    },
                    suffix: { fontSize: 12 }
                }}
                onChange={handleChange}
                onKeyDownCapture={ev => {
                    if (ev.key === 'Enter') {
                        ev.currentTarget.blur()
                    }
                }}
                onBlur={handleBlur}
                {...inputProps}
            />
            <Popover opened={dropdownOpened} disabled={disableUnitChange} width={180} position="bottom-end" onChange={setDropdownOpened}>
                <Popover.Target>
                    <SCxDropdownButton>
                        <IconFont type="ArrowDownSmall" color="var(--color-gray-400)" />
                    </SCxDropdownButton>
                </Popover.Target>
                <Popover.Dropdown>
                    <SelectDropdown
                        options={dropdownOptions}
                        onSelect={v => handleItemClick(v as SizeInputValue['unit'])}
                        itemExtra={option => value?.unit === option.value && <IconFont type="Tick" color="var(--color-main)" />}
                    />
                </Popover.Dropdown>
            </Popover>
        </SCxRectSizeInputWrapper>
    )
}
